import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { useSnackbar } from 'notistack';
import { WORKY_WEB_APP_PATHS } from '../WorkyWebAppPaths';
import { WORKY_BRAND_LOGO_SRC } from '../common/WorkyBrandConstants';
import { isValidString } from '../common/CommonUtilsMethods';
import {
    buildWorkyAuthPathWithEmail,
    readWorkyAuthEmailFromLocationSearch,
} from './WorkyAuthQueryParamService';
import {
    confirmCognitoPasswordReset,
    requestCognitoPasswordReset,
} from './WorkyCognitoAuthService';
import type { CognitoPasswordResetFailureResult } from './WorkyCognitoAuthServiceInterfaces';
import {
    isValidWorkyPassword,
} from './WorkyPasswordValidationService';
import { WorkyPasswordCriteriaChecklist } from './WorkyPasswordCriteriaChecklist';
import { isUnverifiedContactPasswordResetFailure } from './WorkyPasswordResetFailureService';
import './WorkyLoginPage.css';

const WorkyResetPasswordPage = (): JSX.Element => {
    const history = useHistory();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const initialEmailAddress: string = useMemo(
        (): string => readWorkyAuthEmailFromLocationSearch(location.search),
        [location.search]
    );
    const [emailAddress, setEmailAddress] = useState<string>(initialEmailAddress);
    const [confirmationCode, setConfirmationCode] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');
    const [isPasswordCriteriaVisible, setIsPasswordCriteriaVisible] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isResendingCode, setIsResendingCode] = useState<boolean>(false);

    const handleSubmit = useCallback(
        async (submitEvent: React.FormEvent<HTMLFormElement>): Promise<void> => {
            submitEvent.preventDefault();
            if (isSubmitting) {
                return;
            }

            if (!isValidString(emailAddress) || !isValidString(confirmationCode)) {
                enqueueSnackbar('Email and verification code are required.', { variant: 'warning' });
                return;
            }
            if (!isValidWorkyPassword(newPassword)) {
                enqueueSnackbar('Please meet all password requirements before continuing.', {
                    variant: 'warning',
                });
                return;
            }
            if (newPassword !== confirmNewPassword) {
                enqueueSnackbar('Passwords do not match.', { variant: 'warning' });
                return;
            }

            setIsSubmitting(true);
            try {
                const resetFailure: CognitoPasswordResetFailureResult | null =
                    await confirmCognitoPasswordReset({
                        emailAddress,
                        confirmationCode,
                        newPassword,
                    });
                if (resetFailure !== null) {
                    enqueueSnackbar(resetFailure.errorMessage, { variant: 'error' });
                    return;
                }

                enqueueSnackbar('Your password was reset successfully. Please sign in.', {
                    variant: 'success',
                });
                history.replace(
                    buildWorkyAuthPathWithEmail(WORKY_WEB_APP_PATHS.login, emailAddress)
                );
            } finally {
                setIsSubmitting(false);
            }
        },
        [
            confirmNewPassword,
            confirmationCode,
            emailAddress,
            enqueueSnackbar,
            history,
            isSubmitting,
            newPassword,
        ]
    );

    const handleResendCode = useCallback(async (): Promise<void> => {
        if (isResendingCode || !isValidString(emailAddress)) {
            if (!isValidString(emailAddress)) {
                enqueueSnackbar('Please enter your email address.', { variant: 'warning' });
            }
            return;
        }

        setIsResendingCode(true);
        try {
            const resetFailure: CognitoPasswordResetFailureResult | null =
                await requestCognitoPasswordReset(emailAddress);
            if (resetFailure !== null) {
                if (isUnverifiedContactPasswordResetFailure(resetFailure)) {
                    enqueueSnackbar(
                        'Your email is not verified yet. We can send you a verification code.',
                        { variant: 'warning' }
                    );
                    history.push(
                        buildWorkyAuthPathWithEmail(WORKY_WEB_APP_PATHS.verifyEmail, emailAddress)
                    );
                    return;
                }

                enqueueSnackbar(resetFailure.errorMessage, { variant: 'error' });
                return;
            }
            enqueueSnackbar('A new password reset code was sent to your email.', {
                variant: 'success',
            });
        } finally {
            setIsResendingCode(false);
        }
    }, [emailAddress, enqueueSnackbar, history, isResendingCode]);

    const handleNavigateToLogin = useCallback((): void => {
        history.push(
            isValidString(emailAddress)
                ? buildWorkyAuthPathWithEmail(WORKY_WEB_APP_PATHS.login, emailAddress)
                : WORKY_WEB_APP_PATHS.login
        );
    }, [emailAddress, history]);

    return (
        <div className="worky-login-page">
            <button
                type="button"
                className="worky-login-back-home-button"
                onClick={handleNavigateToLogin}
            >
                <ArrowBackIcon fontSize="small" />
                Back to sign in
            </button>
            <div className="worky-login-card">
                <button
                    type="button"
                    className="worky-login-logo-button"
                    onClick={handleNavigateToLogin}
                    aria-label="Worky sign in"
                >
                    <img src={WORKY_BRAND_LOGO_SRC} alt="Worky" className="worky-login-logo-image" />
                </button>

                <h1 className="worky-login-title">Reset password</h1>
                <p className="worky-login-subtitle">
                    Enter the code from your email and choose a new password.
                </p>

                <form className="worky-login-form" onSubmit={handleSubmit}>
                    <TextField
                        label="Email"
                        type="email"
                        autoComplete="email"
                        value={emailAddress}
                        onChange={(changeEvent): void => setEmailAddress(changeEvent.target.value)}
                        required
                        fullWidth
                    />
                    <TextField
                        label="Verification code"
                        autoComplete="one-time-code"
                        value={confirmationCode}
                        onChange={(changeEvent): void => setConfirmationCode(changeEvent.target.value)}
                        required
                        fullWidth
                    />
                    <TextField
                        label="New password"
                        type="password"
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(changeEvent): void => setNewPassword(changeEvent.target.value)}
                        onFocus={(): void => setIsPasswordCriteriaVisible(true)}
                        onBlur={(): void => {
                            if (!isValidString(newPassword)) {
                                setIsPasswordCriteriaVisible(false);
                            }
                        }}
                        required
                        fullWidth
                    />
                    <WorkyPasswordCriteriaChecklist
                        password={newPassword}
                        isVisible={isPasswordCriteriaVisible}
                    />
                    <TextField
                        label="Confirm new password"
                        type="password"
                        autoComplete="new-password"
                        value={confirmNewPassword}
                        onChange={(changeEvent): void => setConfirmNewPassword(changeEvent.target.value)}
                        required
                        fullWidth
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        className="worky-login-submit-button"
                        disabled={isSubmitting || isResendingCode}
                        fullWidth
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Reset password'}
                    </Button>
                    <Button
                        type="button"
                        variant="text"
                        disabled={isSubmitting || isResendingCode}
                        fullWidth
                        onClick={(): void => {
                            void handleResendCode();
                        }}
                    >
                        {isResendingCode ? 'Sending code…' : 'Send another code'}
                    </Button>
                </form>
            </div>
        </div>
    );
};

export { WorkyResetPasswordPage };
